/**
 * Simple Token Expiration Manager
 * Shows popup 1 minute before token expires
 */

import { JwtPayload } from 'app/pages/Auth/slice/types';
import jwtDecode from 'jwt-decode';
import {
  getActiveToken,
  getIdentityToken,
  clearTabToken,
  clearIdentityToken,
} from 'utils/tabAuth';

let checkInterval: NodeJS.Timeout | null = null;
let warningShown = false;
let onWarningCallback: (() => void) | null = null;
// Set when the SERVER rejects a request because the session was revoked
// (logged out on another device / by an admin) — distinct from the JWT's own
// exp, since a revoked token can still be unexpired.
let forceExpired = false;

/** True when the server reported the session as revoked. */
export const isForcedExpired = (): boolean => forceExpired;

/**
 * Force the "session expired" popup immediately. Called from the API layer when
 * any request returns 401 "Session has been logged out", so a session revoked
 * elsewhere logs the user out here too — regardless of the JWT's own expiry.
 */
export const forceSessionExpired = (): void => {
  if (forceExpired) return; // already handling
  forceExpired = true;
  warningShown = true;
  if (onWarningCallback) onWarningCallback();
};

/**
 * Start monitoring token expiration
 */
export const startTokenMonitoring = (onWarning: () => void): void => {
  onWarningCallback = onWarning;
  warningShown = false;

  // Clear any existing interval
  if (checkInterval) {
    clearInterval(checkInterval);
  }

  // Check every 30 seconds
  checkInterval = setInterval(() => {
    checkTokenExpiration();
  }, 30000);

  // Initial check
  checkTokenExpiration();
};

/**
 * Stop monitoring token expiration
 */
export const stopTokenMonitoring = (): void => {
  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
  onWarningCallback = null;
  warningShown = false;
  forceExpired = false;
};

/**
 * Check if token will expire soon and show warning
 */
const checkTokenExpiration = (): void => {
  const token = getActiveToken();
  if (!token) return;

  try {
    const decoded = jwtDecode<JwtPayload>(token.replace('Bearer ', ''));
    const now = Date.now();
    const expTime = decoded.exp * 1000;
    const timeUntilExp = expTime - now;

    // If token expired, show expired popup instead of auto redirect
    if (timeUntilExp <= 0 && !warningShown && onWarningCallback) {
      warningShown = true;
      onWarningCallback();
      return;
    }

    // Show warning 1 minute before expiration
    if (timeUntilExp <= 60000 && !warningShown && onWarningCallback) {
      warningShown = true;
      onWarningCallback();
    }
  } catch (error) {
    console.error('Token validation failed:', error);
    // Show expired popup for invalid tokens too
    if (!warningShown && onWarningCallback) {
      warningShown = true;
      onWarningCallback();
    }
  }
};

export const clearExpiredSessionTokens = (): void => {
  const expired = getActiveToken();
  clearTabToken();
  if (getIdentityToken() === expired) clearIdentityToken();
};

export const handleExpiredToken = (): void => {
  clearExpiredSessionTokens();
  localStorage.removeItem('ApiKey');
  sessionStorage.removeItem('selectedClientWaNumber');
  window.location.href = '/';
};

/**
 * Check if token is already expired
 */
export const isTokenExpired = (): boolean => {
  const token = getActiveToken();
  if (!token) return true;

  try {
    const decoded = jwtDecode<JwtPayload>(token.replace('Bearer ', ''));
    return decoded.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

/**
 * Get time until token expires (in milliseconds)
 */
export const getTimeUntilExpiry = (): number => {
  const token = getActiveToken();
  if (!token) return 0;

  try {
    const decoded = jwtDecode<JwtPayload>(token.replace('Bearer ', ''));
    return decoded.exp * 1000 - Date.now();
  } catch {
    return 0;
  }
};
